import React, { useState, useEffect } from 'react';
import { campo } from './ui/campo';
import { useIdioma } from '../i18n/idioma';
import { Client, ClientTag } from '../types';
import { X, Plus, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import AddressAutocomplete from './AddressAutocomplete';
import { useSettings } from '../contexts/SettingsContext';
import { useGoogleMaps } from '../contexts/GoogleMapsContext';
import { getIndustryConfig } from '../data/industryConfig';
import { validateClientForm, ClientFormErrors } from '../services/clientValidation';

interface ClientModalProps {
  client?: Client;
  /** May be async; if it rejects, the modal stays open and shows the error
   *  (e.g. a duplicate-client block enforced by the mutation). Any resolved
   *  value is ignored. */
  onSave: (client: Omit<Client, 'id' | 'dateCreated' | 'totalSpent'> | Partial<Client>) => void | Promise<unknown>;
  onClose: () => void;
}

const ClientModal: React.FC<ClientModalProps> = ({ client, onSave, onClose }) => {
    const { t } = useIdioma();
  const { settings } = useSettings();
  const { isLoaded: mapsLoaded } = useGoogleMaps();
  const industryConfig = getIndustryConfig(settings.profile.industryType);
  const AVAILABLE_TAGS: ClientTag[] = industryConfig.clientTags as ClientTag[];
  const [customTagInput, setCustomTagInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    propertyNotes: '',
    gateCode: '',
    preferredContact: 'Phone' as 'Phone' | 'Email' | 'Text',
    tags: [] as ClientTag[]
  });
  const [errors, setErrors] = useState<ClientFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Whether the current address text came from picking a Google suggestion.
  // Existing clients are seeded as already-verified.
  const [addressSelected, setAddressSelected] = useState(false);
  // Downgraded by AddressAutocomplete if the Places API can't return results.
  const [addressSearchOk, setAddressSearchOk] = useState(true);
  const addressSearchAvailable = mapsLoaded && addressSearchOk;

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        phone: client.phone,
        email: client.email,
        address: client.address,
        propertyNotes: client.propertyNotes,
        gateCode: client.gateCode,
        preferredContact: client.preferredContact,
        tags: client.tags || []
      });
      setAddressSelected(!!client.address); // stored address is treated as verified
    }
  }, [client]);

  const clearError = (field: keyof ClientFormErrors) =>
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    const validationErrors = validateClientForm(formData, { addressSelected, addressSearchAvailable });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSaving(true);
    try {
      await onSave({ ...formData, name: formData.name.trim(), email: formData.email.trim() });
      onClose();
    } catch (err: any) {
      setSaveError(err?.message || t('clientes.modal.error_guardar'));
    } finally {
      setIsSaving(false);
    }
  };

  const errorTextClass = 'mt-1.5 text-xs font-medium text-peligro flex items-center gap-1';

  const toggleTag = (tag: ClientTag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const addCustomTag = () => {
    const trimmed = customTagInput.trim();
    if (!trimmed) return;
    if (formData.tags.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      setCustomTagInput('');
      return;
    }
    setFormData(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    setCustomTagInput('');
  };

  const removeTag = (tag: ClientTag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const customTags = formData.tags.filter(t => !AVAILABLE_TAGS.includes(t));

  const inputClass = campo({ alto: 'auto' });
  const labelClass = "label-ui block mb-1.5";

  return (
    /* Pulsar fuera cierra. El `onClick` va en la cortina y comprueba que el
       clic haya caído en ELLA y no en algo de dentro: sin ese `===`, cualquier
       clic dentro del formulario burbujea hasta aquí y cierra el modal a media
       escritura. `onMouseDown` no vale — al arrastrar para seleccionar texto
       dentro y soltar fuera, el clic acaba en la cortina y se perdería lo
       escrito. */
    <div
      className="fixed inset-0 bg-navy-950/50 z-[250] flex items-end sm:items-center justify-center sm:p-4 animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-s3 w-full sm:max-w-2xl overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 fade-in duration-300 h-[95dvh] sm:h-auto sm:max-h-[90dvh] flex flex-col">
        <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-acero-200 shrink-0">
          <h3 className="text-lg sm:text-xl font-semibold tracking-tight text-navy-950">{client ? t('clientes.modal.titulo_editar') : t('clientes.modal.titulo_nuevo')}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('clientes.modal.cerrar')}
            className="p-2 rounded-lg text-peligro hover:bg-peligro-bg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={labelClass}>{t('clientes.modal.nombre')}<span className="text-red-500 ml-1" aria-hidden="true">*</span></label>
              <input
                type="text"
                className={inputClass}
                placeholder={t('clientes.modal.nombre_placeholder')}
                value={formData.name}
                aria-invalid={!!errors.name}
                onChange={e => { setFormData({ ...formData, name: e.target.value }); clearError('name'); }}
              />
              {errors.name && <p className={errorTextClass}><AlertCircle size={13} /> {errors.name}</p>}
            </div>

            <div>
              <label className={labelClass}>{t('clientes.modal.telefono')}<span className="text-red-500 ml-1" aria-hidden="true">*</span></label>
              <input
                type="tel"
                inputMode="tel"
                className={inputClass}
                placeholder="(555) 123-4567"
                value={formData.phone}
                aria-invalid={!!errors.phone}
                onChange={e => { setFormData({ ...formData, phone: e.target.value }); clearError('phone'); }}
              />
              {errors.phone && <p className={errorTextClass}><AlertCircle size={13} /> {errors.phone}</p>}
            </div>

            <div>
              <label className={labelClass}>{t('clientes.modal.correo')}<span className="text-red-500 ml-1" aria-hidden="true">*</span></label>
              {/* No HTML `required` here on purpose: none of the other required
                  fields use it, and mixing the browser's native bubble with the
                  form's own inline messages makes one field behave unlike the
                  rest. validateClientForm is the single gate. */}
              <input
                type="email"
                className={inputClass}
                placeholder={t('clientes.modal.correo_placeholder')}
                value={formData.email}
                aria-required="true"
                aria-invalid={!!errors.email}
                onChange={e => { setFormData({ ...formData, email: e.target.value }); clearError('email'); }}
              />
              {errors.email && <p className={errorTextClass}><AlertCircle size={13} /> {errors.email}</p>}
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>{t('clientes.modal.direccion')}<span className="text-red-500 ml-1" aria-hidden="true">*</span></label>
              <AddressAutocomplete
                value={formData.address}
                onChange={(val) => { setFormData(prev => ({ ...prev, address: val })); setAddressSelected(false); clearError('address'); }}
                onPlaceSelect={() => { setAddressSelected(true); clearError('address'); }}
                onSearchAvailabilityChange={setAddressSearchOk}
                className={`${inputClass} pl-10`}
                iconClassName="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400"
                iconSize={18}
                placeholder={t('clientes.modal.direccion_placeholder')}
              />
              {errors.address && <p className={errorTextClass}><AlertCircle size={13} /> {errors.address}</p>}
            </div>

            <div>
              <label className={labelClass}>{t('clientes.modal.gate')}</label>
              <input
                type="text"
                maxLength={40}
                className={inputClass}
                placeholder="#1234"
                value={formData.gateCode}
                onChange={e => setFormData({ ...formData, gateCode: e.target.value })}
              />
            </div>

            <div>
              <label className={labelClass}>{t('clientes.modal.contacto')}</label>
              <div className="relative">
                <select
                  className={`${inputClass} appearance-none`}
                  value={formData.preferredContact}
                  onChange={e => setFormData({ ...formData, preferredContact: e.target.value as any })}
                >
                  <option value="Phone">{t('clientes.modal.telefono')}</option>
                  <option value="Email">{t('clientes.modal.correo')}</option>
                  <option value="Text">{t('clientes.modal.contacto_texto')}</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-acero-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>{t('clientes.modal.notas')}</label>
              <textarea
                className={`${inputClass} min-h-[80px] resize-none`}
                rows={3}
                placeholder={t('clientes.modal.notas_placeholder')}
                value={formData.propertyNotes}
                onChange={e => setFormData({ ...formData, propertyNotes: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>{t('clientes.modal.tags')}</label>
              <div className="flex flex-wrap gap-2.5">
                {AVAILABLE_TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    aria-pressed={formData.tags.includes(tag)}
                    className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${formData.tags.includes(tag)
                      ? 'bg-navy-850 text-white border-navy-850'
                      : 'bg-white text-acero-700 border-acero-300 hover:border-acero-400 hover:bg-acero-50'
                      }`}
                  >
                    {tag}
                  </button>
                ))}
                {customTags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-brand-400 text-navy-950 border border-brand-400"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:bg-navy-950/10 rounded p-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-850"
                      aria-label={t('comun.quitarAria', { que: tag })}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>

              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  className={inputClass}
                  placeholder={t('clientes.modal.tag_placeholder')}
                  value={customTagInput}
                  onChange={e => setCustomTagInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={addCustomTag}
                  disabled={!customTagInput.trim()}
                  leftIcon={<Plus size={16} />}
                  className="shrink-0"
                >{t('clientes.modal.agregar')}
                </Button>
              </div>
            </div>
          </div>

          {saveError && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-peligro-bg border border-peligro/25 text-sm font-medium text-peligro">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{saveError}</span>
            </div>
          )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end px-4 sm:px-8 pt-4 sm:pt-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-6 border-t border-acero-200 bg-acero-50 gap-2 sm:gap-3 shrink-0">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving} className="w-full sm:w-auto">
              {t('clientes.modal.cancelar')}
            </Button>
            <Button type="submit" variant="primary" isLoading={isSaving} className="w-full sm:w-auto">
              {isSaving ? t('clientes.modal.guardando') : client ? t('clientes.modal.actualizar') : t('clientes.modal.crear')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;